import Big from 'big.js';
import { get } from 'lodash';
import { useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import ActivityStatusBadge from '@/modules/common/components/ActivityStatusBadge.tsx';
import { DateVariant, dateToPrint } from '@/modules/common/utils/date-format.ts';
import { EntityWithDocumentFullResponse } from '@/modules/entity/types/EntityItemResponse.ts';
import { IssuerConfirmTransferWithLoadByIdModal } from '@/modules/issuer-dashboard/components/modals/IssuerConfirmTransferWithLoadByIdModal.tsx';
import {
  InfoItem,
  TransactionsDocuments,
} from '@/modules/issuer-dashboard/components/modals/IssuerTransactionsOperationInfoModal.tsx';
import {
  useIssuerTransferByIdQuery,
  useTransferSetRejectedMutation,
} from '@/modules/issuer-dashboard/queries/transfers.tsx';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';
import { Badge, BaseModalProps, Button, DeclineModal, ModalContentWrapper, Spinner } from 'ui';
import { EditLightGrayIcon } from 'ui/icons';
import {
  goodFormatNumber,
  goodFormatUSDCurrency,
  goodFormatUSDCurrencyLowPrecision,
} from 'ui/utils';
import { TransactionStatus, TransferPaginateResponse, TransferSetRejectedStatusRequest } from 'api';

const TransferInformation = ({ transfer }: { transfer: TransferPaginateResponse }) => {
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const { to_shareholder_entity, from_shareholder_entity } = transfer;
  const toEntity = to_shareholder_entity as unknown as EntityWithDocumentFullResponse;
  const fromEntity = from_shareholder_entity as unknown as EntityWithDocumentFullResponse;
  const toEntityName =
    ('business_name_trustee' in (toEntity?.smsf_entity_details || {}) &&
      !!toEntity?.smsf_entity_details?.business_name_trustee) ||
    ('business_name_trustee' in (toEntity?.trust_entity_details || {}) &&
      !!toEntity?.trust_entity_details?.business_name_trustee)
      ? `${
          toEntity?.trust_entity_details?.business_name_trustee ||
          toEntity?.smsf_entity_details?.business_name_trustee
        } <${toEntity?.entity_name}>`
      : `${toEntity?.entity_name}`;

  const fromEntityName =
    ('business_name_trustee' in (fromEntity?.smsf_entity_details || {}) &&
      !!fromEntity?.smsf_entity_details?.business_name_trustee) ||
    ('business_name_trustee' in (fromEntity?.trust_entity_details || {}) &&
      !!fromEntity?.trust_entity_details?.business_name_trustee)
      ? `${
          fromEntity?.trust_entity_details?.business_name_trustee ||
          fromEntity?.smsf_entity_details?.business_name_trustee
        } <${fromEntity?.entity_name}>`
      : `${fromEntity?.entity_name}`;

  return (
    <>
      <InfoItem title="Sender">
        <Link
          state={{ from: pathname + '?' + params.toString() }}
          to={{
            pathname: issuerDashboardPaths.dynamic.holder((fromEntity?.id || '0').toString()),
            search: params.toString(),
          }}
          className="text-blue-700 hover:underline"
        >
          {fromEntityName}
        </Link>
      </InfoItem>
      <InfoItem title="Receiver">
        <Link
          state={{ from: pathname + '?' + params.toString() }}
          to={{
            pathname: issuerDashboardPaths.dynamic.holder((toEntity?.id || '0').toString()),
            search: params.toString(),
          }}
          className="text-blue-700 hover:underline"
        >
          {toEntityName}
        </Link>
      </InfoItem>
    </>
  );
};

interface IssuerTransferOperationInfoModalProps extends BaseModalProps {
  id: number;
  isDeleted?: boolean;
}

export const IssuerTransferOperationInfoModal = ({
  id,
  isDeleted = false,
  open,
  onClose,
}: IssuerTransferOperationInfoModalProps) => {
  const [openConfirm, setOpenConfirm] = useState(false);
  const [openReject, setOpenReject] = useState(false);
  const { data, isLoading: isOperationLoading } = useIssuerTransferByIdQuery({
    id,
    is_deleted: isDeleted,
  });
  const { mutateAsync: rejectTransfer } = useTransferSetRejectedMutation();

  const operationData = get(data, 'transfer') as TransferPaginateResponse;

  const completionDate = get(operationData, 'confirmation_date', get(operationData, 'issue_date'));

  return (
    <ModalContentWrapper
      variant="right"
      title={
        <div className="flex gap-4 items-center">
          <span>Transfer info</span>
          {isDeleted && (
            <Badge className="bg-red-400 text-white text-xs font-[500] font-[Inter]">
              Archived
            </Badge>
          )}
        </div>
      }
      open={open}
      onClose={onClose}
    >
      {isOperationLoading && (
        <div className="w-full h-full flex justify-center items-center">
          <Spinner size="lg" />
        </div>
      )}
      {!isOperationLoading && operationData && (
        <>
          <div className="flex flex-col justify-between gap-4 h-full">
            <div className="space-y-3">
              <InfoItem title="ID">
                <div>#{operationData.id}</div>
              </InfoItem>
              <InfoItem title="Status">
                <ActivityStatusBadge status={operationData.status} />
              </InfoItem>
              <InfoItem title="Class">
                <div>{operationData.register_class.class_name}</div>
              </InfoItem>

              <TransferInformation transfer={operationData as TransferPaginateResponse} />

              <InfoItem title="Units/shares">
                <div>{goodFormatNumber(operationData.quantity)}</div>
              </InfoItem>
              <InfoItem title="Price">
                <div>{goodFormatUSDCurrency(operationData.price_per_share)}</div>
              </InfoItem>
              <InfoItem title="Total">
                <div>
                  {goodFormatUSDCurrencyLowPrecision(
                    +new Big(operationData.quantity)
                      ?.mul(operationData.price_per_share || 0)
                      ?.valueOf(),
                  )}
                </div>
              </InfoItem>
              <InfoItem title="Requested date">
                <div>{dateToPrint(operationData.requested_at_date, DateVariant.Information)}</div>
              </InfoItem>
              <InfoItem title="Completion date">
                {operationData?.status === TransactionStatus.Completed
                  ? dateToPrint(completionDate, DateVariant.Information)
                  : '-'}
              </InfoItem>
              <InfoItem title="Memo">
                <div>{operationData.memo || '-'}</div>
              </InfoItem>

              {!isDeleted && (
                <TransactionsDocuments
                  transferId={id}
                  status={operationData?.status}
                  document_storage_item_urls={operationData?.document_storage_item_urls}
                />
              )}
            </div>
            {!isDeleted && (
              <div className="flex flex-col gap-4 w-full">
                {operationData?.status === TransactionStatus.Pending && (
                  <>
                    <Button
                      className="min-w-[90px] flex gap-4 items-center"
                      onClick={() => {
                        setOpenConfirm(true);
                      }}
                    >
                      <img alt="Edit" src={EditLightGrayIcon} className="w-5 h-5 min-w-5 min-h-5" />{' '}
                      Open confirmation window
                    </Button>

                    <Button
                      variant="secondary"
                      className="min-w-[90px]"
                      onClick={() => {
                        setOpenReject(true);
                      }}
                    >
                      Decline
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
          <IssuerConfirmTransferWithLoadByIdModal
            transferId={operationData?.id}
            open={openConfirm}
            onClose={() => setOpenConfirm(false)}
          />
          <DeclineModal
            onClose={() => setOpenReject(false)}
            open={openReject}
            onConfirm={async (payload: TransferSetRejectedStatusRequest) => {
              await rejectTransfer({ ...payload, id: operationData?.id });
            }}
          />
        </>
      )}
    </ModalContentWrapper>
  );
};
